import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useState } from "react";
import { useAuth } from "@/auth/auth-context";
import { track } from "@/lib/analytics";
import "./login-screen.scss";
/**
 * Normalizes a phone number for API submission.
 * If the number is exactly 10 digits, prepend +1 (US country code).
 */
function normalizePhoneNumber(phone) {
    const digitsOnly = phone.replace(/\D/g, "");
    if (digitsOnly.length === 10) {
        return `+1${digitsOnly}`;
    }
    if (!phone.startsWith("+")) {
        return `+${digitsOnly}`;
    }
    return phone;
}
export const LoginScreen = () => {
    const { sendPhoneCode, verifyPhoneCode, sendEmailCode, verifyEmailCode } = useAuth();
    const [loginMethod, setLoginMethod] = useState("phone");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [accessCode, setAccessCode] = useState("");
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState("input");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [localError, setLocalError] = useState();
    const handleSendCode = useCallback(async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setLocalError(undefined);
        try {
            if (loginMethod === "phone") {
                await sendPhoneCode(normalizePhoneNumber(phoneNumber), accessCode);
            }
            else {
                await sendEmailCode(email, accessCode);
            }
            track({
                name: "auth.identifier_entered",
                props: { method: loginMethod === "phone" ? "phone" : "email" },
            });
            setStep("code");
        }
        catch (e) {
            track({ name: "auth.failed", props: { step: "identifier" } });
            setLocalError(e && typeof e === "object" && "message" in e
                ? e.message
                : "Something went wrong. Please try again.");
        }
        finally {
            setIsSubmitting(false);
        }
    }, [loginMethod, phoneNumber, email, accessCode, sendPhoneCode, sendEmailCode]);
    const handleVerifyCode = useCallback(async (event) => {
        event.preventDefault();
        setIsSubmitting(true);
        setLocalError(undefined);
        try {
            if (loginMethod === "phone") {
                await verifyPhoneCode(normalizePhoneNumber(phoneNumber), accessCode, otp);
            }
            else {
                await verifyEmailCode(email, accessCode, otp);
            }
            track({ name: "auth.otp_entered", props: {} });
            track({ name: "auth.succeeded", props: {} });
        }
        catch (e) {
            track({ name: "auth.failed", props: { step: "otp" } });
            setLocalError(e && typeof e === "object" && "message" in e
                ? e.message
                : "Something went wrong. Please try again.");
        }
        finally {
            setIsSubmitting(false);
        }
    }, [loginMethod, phoneNumber, email, accessCode, otp, verifyPhoneCode, verifyEmailCode]);
    const switchMethod = useCallback((method) => {
        setLoginMethod(method);
        setStep("input");
        setOtp("");
        setLocalError(undefined);
    }, []);
    const startOver = useCallback(() => {
        setStep("input");
        setOtp("");
        setLocalError(undefined);
    }, []);
    return (_jsxs("div", { className: "login-screen", children: [localError ? _jsx("p", { className: "login-error", children: localError }) : undefined, step === "input" ? (_jsxs(_Fragment, { children: [_jsxs("form", { onSubmit: handleSendCode, className: "login-form", children: [loginMethod === "phone" ? (_jsx("label", { children: _jsx("input", { type: "tel", autoComplete: "tel", value: phoneNumber, onChange: (e) => setPhoneNumber(e.target.value), placeholder: "555 123 4567", required: true }) })) : (_jsx("label", { children: _jsx("input", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: "you@example.com", required: true }) })), _jsx("label", { children: _jsx("input", { type: "password", value: accessCode, onChange: (e) => setAccessCode(e.target.value), placeholder: "Access code", autoComplete: "off" }) }), _jsx("button", { type: "submit", disabled: isSubmitting, children: isSubmitting ? "Sending..." : loginMethod === "phone" ? "Send SMS" : "Send code" })] }), _jsx("button", { type: "button", className: "login-toggle", onClick: () => switchMethod(loginMethod === "phone" ? "email" : "phone"), children: loginMethod === "phone" ? "Use email instead" : "Use phone instead" })] })) : (_jsxs(_Fragment, { children: [_jsxs("form", { onSubmit: handleVerifyCode, className: "login-form", children: [_jsx("label", { children: _jsx("input", { type: "text", inputMode: "numeric", autoComplete: "one-time-code", value: otp, onChange: (e) => setOtp(e.target.value), placeholder: "123456", required: true }) }), _jsx("button", { type: "submit", disabled: isSubmitting, children: isSubmitting ? "Verifying..." : "Verify code" })] }), _jsx("button", { type: "button", className: "login-toggle", onClick: startOver, children: "Start over" })] }))] }));
};
